import dayjs from 'dayjs';
import { SCHEDULE_PATTERNS } from '../../constants/schedule';
import { getNewSiteData } from '../../helper/site';
import { displayDateTimeInSpecifiedTZ, hourMinuteToObject } from '../../time';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { everyDaySchedule } from '../../../mocks/utils';
import { isEmpty } from 'lodash';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(timezone);

export const getScheduleFormSiteValue = ({
  regionId,
  regionName,
  companyId,
  companyName,
  businessClients,
  rawClients,
  values: { newSite, ...data },
} = {}) => {
  if (newSite) {
    const { siteName, siteLocations, ...siteData } = getNewSiteData({
      newSite,
      regionId,
      regionName,
      companyId,
      companyName,
      businessClients,
      rawClients,
      values: { ...data, newSite },
    });
    return {
      siteData: { name: siteName, locations: siteLocations, ...siteData },
    };
  }

  if (data.site) {
    return {
      businessClientId: data.site.businessClientId,
      siteId: data.site.id,
    };
  }
  // need to change this after changes in initial values
  return { businessClientId: data.businessClientId, siteId: data.siteId };
};

export const getWeeklyTime = (startTime, endTime) => {
  if (dayjs(startTime).isValid() && dayjs(endTime).isValid)
    return { startAfter: startTime, finishBefore: endTime };

  const sdate = hourMinuteToObject(startTime);
  const edate = hourMinuteToObject(endTime);

  const startDate = dayjs()
    .set('hour', sdate.hour)
    .set('minute', sdate.minute);
  const endDate = dayjs()
    .set('hour', edate.hour)
    .set('minute', edate.minute);

  const startDateIsMore = startDate.isSameOrAfter(endDate);

  return {
    startAfter: startDate,
    finishBefore: startDateIsMore ? endDate.add(1, 'day') : endDate,
  };
};

const combineDateAndTime = (dateToConvert, time, scheduleDataTimezone) => {
  const date = hourMinuteToObject(time);
  // add hours, minutes and timezone to the date
  const combineDate = dayjs(dateToConvert)
    .add(date.hour, 'h')
    .add(date.minute, 'm');
  // order of params -> dayjs date object --- regionalTimZone ----selectedTimeZone
  const transformedDateTime = transformDateToSelectedTimeZoneUTC(
    combineDate,
    scheduleDataTimezone,
  );
  return transformedDateTime;
};
// ---------------Can be used-------------------------------------------------
// export const dateWithTimeZone = (
//   timeZone,
//   year,
//   month,
//   day,
//   hour,
//   minute,
//   second,
// ) => {
//   let date = new Date(Date.UTC(year, month, day, hour, minute, second));

//   let utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
//   let tzDate = new Date(date.toLocaleString('en-US', { timeZone: timeZone }));
//   let offset = utcDate.getTime() - tzDate.getTime();

//   date.setTime(date.getTime() + offset);

//   return date;
// };

export const getOffsetBetweenTimezones = (date, timezone1, timezone2) => {
  if (!timezone1 || timezone1 === '') {
    //if the timezone is null or blank there is no need to calculate the offset.
    return 0;
  }
  const timezone1Date = convertDateToAnotherTimeZone(date, timezone1);
  //timezone 2 is never null as we must have a timezone set
  const timezone2Date = convertDateToAnotherTimeZone(date, timezone2);
  const diff = (timezone1Date.getTime() - timezone2Date.getTime()) / 60000;
  return Math.floor(diff);
};

function convertDateToAnotherTimeZone(date, timezone) {
  const dateString = date.toLocaleString('en-US', {
    timeZone: timezone,
  });
  return new Date(dateString);
}
/**
 * @param {object} dateObj - This can be an object when selected from a calander or a string when displayed in edit mode
 * @param {string} selectedTimeZone - specified timezone
 * @returns {string} display string
 */
export const transformDateToSelectedTimeZoneUTC = (
  dateObj, // this can be a day js object or a string
  selectedTimeZone,
) => {
  // js date object
  const jsDateObject = new Date(dateObj);
  const computerTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone; // computer timezone
  // convert selected date time to utc string
  const utcString =
    typeof dateObj === 'string'
      ? jsDateObject.toUTCString()
      : dateObj.utc().format();
  // offset between selected and localtimezone
  const offset = getOffsetBetweenTimezones(
    jsDateObject,
    computerTimezone,
    selectedTimeZone,
  );
  // utc time according to selected Timezone
  const offsetDateTime = dayjs
    .utc(utcString)
    .utcOffset(offset) // calculate this using local timezone and selected timezone
    .format();

  // add zero offset 'Z'
  // The Z stands for the Zero timezone, as it is offset by 0 from the Coordinated Universal Time (UTC).
  // 19 is the length of the format that we get offset time in -> 2022-06-04T16:30:00+4:00. It is always going to
  // this number even if the timezone will be behind UTC(-) or ahead of UTC(+)
  const zeroOffsetDateTime = offsetDateTime.substring(0, 19).concat('Z');

  return zeroOffsetDateTime;
};

export const transformMultipleSchedule = ({ values = [], timezone }) => {
  return values.map(item => {
    const assingees =
      item.assignToUserIDs.length === 1 && item.assignToUserIDs[0].id === 'ALL'
        ? []
        : item.assignToUserIDs;

    const scheduleData = item?.schedule[item?.schedule?.activeTab];

    // get the user provided timezone
    const scheduleDataTimezone = scheduleData?.timezone;

    // startDate + startTime
    const startAfter = scheduleData?.startDate
      ? combineDateAndTime(
          scheduleData.startDate,
          scheduleData.startTimeOnly,
          scheduleDataTimezone,
          timezone,
        )
      : null;
    // startDate + finishTime
    const finishBefore = scheduleData?.startDate
      ? combineDateAndTime(
          scheduleData.startDate,
          scheduleData.endTimeOnly,
          scheduleDataTimezone,
          timezone,
        )
      : null;
    // repeatUntilDate + finish time
    const repeatUntil = scheduleData?.repeatUntil
      ? combineDateAndTime(
          scheduleData.repeatUntil,
          scheduleData.endTimeOnly,
          scheduleDataTimezone,
          timezone,
        )
      : null;
    const { scheduleType } = item;
    const rescheduleWeekends = scheduleData?.rescheduleWeekends;
    const rescheduleInvalidDates = scheduleData?.rescheduleInvalidDates;
    const daysOfMonth = scheduleData?.dateOfMonth
      ? [
          scheduleData.dateOfMonth.length === 1
            ? `0${scheduleData.dateOfMonth}`
            : scheduleData.dateOfMonth,
        ]
      : null;
    const nthWeekdayOfMonth = scheduleData?.dayOfMonth
      ? scheduleData.dayOfMonth
      : null;

    const scheduleOptions = item => {
      const zeroOffsetStart = scheduleData?.startAfter
        ? transformDateToSelectedTimeZoneUTC(
            scheduleData.startAfter,
            scheduleDataTimezone, // user provided timezone
          )
        : null;

      const zeroOffsetFinish = scheduleData?.finishBefore
        ? transformDateToSelectedTimeZoneUTC(
            scheduleData.finishBefore,
            scheduleDataTimezone,
          )
        : null;

      switch (item.scheduleType) {
        case SCHEDULE_PATTERNS.weekly.id:
          return {
            startAfter,
            finishBefore,
            repeatUntil,
          };
        case SCHEDULE_PATTERNS.dateOfMonth.id:
          return {
            startAfter,
            finishBefore,
            repeatUntil,
            daysOfMonth,
            rescheduleInvalidDates,
            rescheduleWeekends,
          };
        case SCHEDULE_PATTERNS.dayOfMonth.id:
          return {
            startAfter,
            finishBefore,
            repeatUntil,
            nthWeekdayOfMonth,
          };
        default:
          // for onceoff or simple interval
          return {
            // convert to the user selected timezone if provided before sending to server
            finishBefore: zeroOffsetFinish,
            startAfter: zeroOffsetStart,
          };
      }
    };

    return {
      id: item.id,
      assignToUserIDs: assingees.map(item => item.id || item),
      daysOfWeek: scheduleData.days,
      ...scheduleOptions(item),
      scheduleType,
      intervalInMinutes: scheduleData.interval,
      repeatEvery: scheduleData?.repeatsEvery,
      ...(scheduleDataTimezone
        ? { timezone: scheduleDataTimezone }
        : { timezone }),
      archived: item.archived,
      ...(!isEmpty(item.location) && {
        surfaceU: item.location[0].lng,
        surfaceV: item.location[0].lat,
      }),
    };
  });
};

export const transformScheduleByPattern = ({ scheduleType, data }) => {
  const pattern = Object.values(SCHEDULE_PATTERNS).find(
    p => p.id === scheduleType,
  );

  if (!pattern || !data) {
    return { schedule: null };
  }

  const baseValue = {
    finishBefore: data.finishBefore
      ? displayDateTimeInSpecifiedTZ({
          timestamp: data.finishBefore,
          timezone: data.timezone,
        })
      : null,
    startAfter: data.startAfter
      ? displayDateTimeInSpecifiedTZ({
          timestamp: data.startAfter,
          timezone: data.timezone,
        })
      : null,
    ...(data?.timezone && { timezone: data.timezone }),
  };

  const schedule = {
    activeTab: pattern.label,
  };

  switch (pattern.label) {
    case SCHEDULE_PATTERNS.simpleInterval.label:
      schedule[pattern.label] = {
        ...baseValue,
        interval: data.intervalInMinutes,
      };

      break;
    case SCHEDULE_PATTERNS.weekly.label: {
      const startTimeOnly = data.startAfter
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.startAfter,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';

      const startHrMin = hourMinuteToObject(startTimeOnly);

      const endTimeOnly = data?.finishBefore
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.finishBefore,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';
      const endHrMin = hourMinuteToObject(endTimeOnly);
      const timezone = data?.timezone;
      // start date -- subtract the start time from date
      const startDate = dayjs(data.startAfter)
        .subtract(startHrMin.hour, 'hour')
        .subtract(startHrMin.minute, 'minute');

      // repeatUntil - subtract end time from date
      const repeatUntil = data?.repeatUntil
        ? dayjs(data.repeatUntil)
            .subtract(endHrMin.hour, 'hour')
            .subtract(endHrMin.minute, 'minute')
        : '';
      // repeatsEvery
      const repeatsEvery = data?.repeatEvery;
      // timezone
      schedule[pattern.label] = {
        startTimeOnly,
        endTimeOnly,
        interval: data.intervalInMinutes,
        days: data.daysOfWeek,
        everyday: everyDaySchedule.every(item =>
          data.daysOfWeek.some(s => s.includes(item)),
        ),
        allDay: startTimeOnly === '00:00' && endTimeOnly === '23:59',
        timezone,
        startDate,
        repeatUntil,
        repeatsEvery,
      };

      break;
    }

    case SCHEDULE_PATTERNS.dateOfMonth.label: {
      const startTimeOnly = data.startAfter
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.startAfter,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';
      const startHrMin = hourMinuteToObject(startTimeOnly);
      const endTimeOnly = data.finishBefore
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.finishBefore,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';
      const endHrMin = hourMinuteToObject(endTimeOnly);

      const timezone = data?.timezone;
      // start date
      const startDate = dayjs(data.startAfter)
        .subtract(startHrMin.hour, 'hour')
        .subtract(startHrMin.minute, 'minute');

      // repeatUntil
      const repeatUntil = data?.repeatUntil
        ? dayjs(data.repeatUntil)
            .subtract(endHrMin.hour, 'hour')
            .subtract(endHrMin.minute, 'minute')
        : '';
      // repeatsEvery
      const repeatsEvery = data?.repeatEvery;
      //backend returns date of months as ['09'], so it needs to be parsed to int to compare to a number
      const dateOfMonth = data?.daysOfMonth
        ? parseInt(data.daysOfMonth[0], 10) < 10
          ? data.daysOfMonth[0].replace('0', '')
          : data.daysOfMonth[0]
        : null;
      const rescheduleWeekends = data?.rescheduleWeekends;
      const rescheduleInvalidDates = data?.rescheduleInvalidDates;

      schedule[pattern.label] = {
        startTimeOnly,
        endTimeOnly,
        interval: data.intervalInMinutes,
        days: data.daysOfWeek,
        allDay: startTimeOnly === '00:00' && endTimeOnly === '23:59',
        timezone,
        startDate,
        repeatUntil,
        repeatsEvery,
        dateOfMonth,
        rescheduleWeekends,
        rescheduleInvalidDates,
      };

      break;
    }

    case SCHEDULE_PATTERNS.dayOfMonth.label: {
      const startTimeOnly = data.startAfter
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.startAfter,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';
      const startHrMin = hourMinuteToObject(startTimeOnly);

      const endTimeOnly = data.finishBefore
        ? displayDateTimeInSpecifiedTZ({
            timestamp: data.finishBefore,
            format: 'HH:mm',
            timezone: data.timezone,
          })
        : '';
      const endHrMin = hourMinuteToObject(endTimeOnly);

      const timezone = data?.timezone;
      // start date
      const startDate = dayjs(data.startAfter)
        .subtract(startHrMin.hour, 'hour')
        .subtract(startHrMin.minute, 'minute');

      // repeatUntil
      const repeatUntil = data?.repeatUntil
        ? dayjs(data.repeatUntil)
            .subtract(endHrMin.hour, 'hour')
            .subtract(endHrMin.minute, 'minute')
        : '';
      // repeatsEvery
      const repeatsEvery = data?.repeatEvery;

      const dayOfMonth = data?.nthWeekdayOfMonth
        ? data.nthWeekdayOfMonth
        : null;

      schedule[pattern.label] = {
        startTimeOnly,
        endTimeOnly,
        interval: data.intervalInMinutes,
        days: data.daysOfWeek,
        allDay: startTimeOnly === '00:00' && endTimeOnly === '23:59',
        timezone,
        startDate,
        repeatUntil,
        repeatsEvery,
        dayOfMonth,
      };

      break;
    }
    default:
      schedule[pattern.label] = baseValue;
      break;
  }

  return schedule;
};
